/* Created by Interactive 63 - https://interactive63.com */
/**
 * Per-track repeating. A repeating track's keyframes describe one cycle; this module expands the
 * cycles into the scene-length keyframe list the compiler and sampler use, and maps scene times
 * back into the first cycle for editing. The expanded keyframes are never stored.
 */
import { mirrorEasing } from './reverse';
import { sampleKeyframeList } from './interpolate';
import { reversedHandles } from './motion-curve';
import type { Keyframe, Track } from '../model/types';
import { PROPERTIES } from '../properties/registry';

export const MAX_EXPANDED_KEYFRAMES = 1000;
export const MIN_PERIOD_MS = 100;
/** Fraction of the scene duration left between a cycle's end and the next cycle's start. */
const SEAM_GAP = 0.00002;

/** Cycle length in ms for a repeating track, or undefined if it does not repeat. */
export function repeatPeriod(track: Track): number | undefined {
  if (!track.repeat) return undefined;
  if (track.repeat.period !== null) return track.repeat.period;
  const last = track.keyframes[track.keyframes.length - 1];
  return Math.max(MIN_PERIOD_MS, last?.time ?? 0);
}

/** The keyframes of one cycle, mirrored for odd cycles of an alternating repeat. */
function cycleKeyframes(base: readonly Keyframe[], period: number, reversed: boolean): Keyframe[] {
  if (!reversed) return base.map((kf) => ({ ...kf }));
  const n = base.length;
  return [...base].reverse().map((kf, j) => {
    const source = base[n - 2 - j];
    return {
      ...kf,
      time: period - kf.time,
      easing: j < n - 1 && source ? mirrorEasing(source.easing) : kf.easing,
      ...(kf.motion ? { motion: reversedHandles(kf.motion) } : {}),
    };
  });
}

/**
 * The keyframes that actually play across the scene: the track's own keyframes when it does not
 * repeat, otherwise its cycles expanded to the scene duration. `capped` is true when the
 * expansion stopped early because it reached the keyframe limit.
 */
export function effectiveKeyframes(
  track: Track,
  duration: number,
): { keyframes: Keyframe[]; capped: boolean } {
  const period = repeatPeriod(track);
  const repeat = track.repeat;
  if (!repeat || period === undefined || track.keyframes.length === 0) {
    return { keyframes: track.keyframes, capped: false };
  }

  const valueType = PROPERTIES[track.property].valueType;
  const cycles = Math.min(
    repeat.count === 'infinite' ? Infinity : repeat.count,
    Math.ceil(duration / period),
  );
  const result: Keyframe[] = [];
  let capped = false;

  for (let k = 0; k < cycles; k += 1) {
    const offset = k * period;
    const items = cycleKeyframes(track.keyframes, period, repeat.alternate && k % 2 === 1);
    const first = items[0];
    if (first && first.time > 0) {
      items.unshift({ id: '', time: 0, value: first.value, easing: { type: 'linear' } });
    }
    const last = items[items.length - 1];
    if (last) items[items.length - 1] = { ...last, easing: { type: 'hold' } };

    const absolute = items.map((kf, i) => ({
      ...kf,
      id: k === 0 && kf.id !== '' ? kf.id : `${track.id}~${k}~${i}`,
      time: offset + kf.time,
    }));

    const fits = offset + period <= duration;
    const kept = fits ? absolute : absolute.filter((kf) => kf.time <= duration);
    if (!fits) {
      const cut = duration - offset;
      const value = sampleKeyframeList(valueType, items, cut);
      const lastKept = kept[kept.length - 1];
      if (value !== undefined && (!lastKept || lastKept.time < duration)) {
        kept.push({
          id: `${track.id}~${k}~cut`,
          time: duration,
          value,
          easing: { type: 'linear' },
        });
      }
    }

    // CSS cannot hold two keyframes at one time, so where a cycle starts exactly when the last one
    // ends, the earlier keyframe moves back by a sliver of the scene (0.002%, wide enough to
    // survive percentage rounding). Its hold easing keeps its value until the new cycle's first
    // keyframe takes over, which gives a clean jump (or no change if the values match).
    const gap = duration * SEAM_GAP;
    const merged = [...result];
    for (const kf of kept) {
      const previous = merged[merged.length - 1];
      if (previous?.time === kf.time) {
        const before = merged[merged.length - 2];
        if (before && before.time < previous.time - gap) {
          merged[merged.length - 1] = { ...previous, time: previous.time - gap };
        } else {
          merged.pop();
        }
      }
      merged.push(kf);
    }
    if (merged.length > MAX_EXPANDED_KEYFRAMES) {
      capped = true;
      break;
    }
    result.length = 0;
    result.push(...merged);
  }
  return { keyframes: result, capped };
}

/**
 * Maps a scene time to the equivalent time inside cycle 0, and whether that cycle plays
 * backwards. Times past the last cycle map to the end of the final cycle.
 */
export function cycleTime(track: Track, time: number): { time: number; reversed: boolean } {
  const period = repeatPeriod(track);
  const repeat = track.repeat;
  if (!repeat || period === undefined || time < period) return { time, reversed: false };

  const count = repeat.count === 'infinite' ? Infinity : repeat.count;
  const index = Math.min(Math.floor(time / period), count - 1);
  const reversed = repeat.alternate && index % 2 === 1;
  const relative = index === count - 1 && time >= count * period ? period : time - index * period;
  return { time: reversed ? period - relative : relative, reversed };
}

/**
 * Times of the later cycles' copies of a repeating track's keyframes, for drawing ghost
 * keyframes on the timeline. The first cycle's own keyframes are not included.
 */
export function ghostTimes(track: Track, duration: number): number[] {
  const period = repeatPeriod(track);
  const repeat = track.repeat;
  if (!repeat || period === undefined) return [];
  const cycles = Math.min(
    repeat.count === 'infinite' ? Infinity : repeat.count,
    Math.ceil(duration / period),
    400,
  );
  const times: number[] = [];
  for (let k = 1; k < cycles; k += 1) {
    for (const kf of track.keyframes) {
      const time = k * period + (repeat.alternate && k % 2 === 1 ? period - kf.time : kf.time);
      if (time <= duration) times.push(time);
    }
  }
  return times.sort((a, b) => a - b);
}
